// The networks of a node (epic nobgp-router#2134, `docs/PRD-multi-network-nodes.md`): none, one or
// several. Pure, so that `node --experimental-strip-types` can test it.

/** A row of `node_networks`, embedded in a `nodes` read. `camelcaseKeys` leaves it as it came. */
type Membership = { network_id: string; name?: string | null; created_at?: string | null }

/**
 * A `nodes` row as a `NodeRecord`: its `node_networks` rows become `networkIds` and `networkNames`.
 *
 * ⚠⚠ THE ONE PLACE THAT READS THE MEMBERSHIPS (router#1767). Each read that puts a row in `useNode`
 * goes through here. `nodes.network_id` is a copy that goes with router#2142: only a realtime event reads it, as a hint.
 *
 * ⚠ The first id is the network the node joined first, the router's order: `created_at`, then the
 * id. `networkHues` depends on that order.
 */
export const withNetworkIds = <R extends object>(
  row: R
): Omit<R, 'nodeNetworks'> & { networkIds: string[]; networkNames: Record<string, string> } => {
  const { nodeNetworks, ...rest } = row as R & { nodeNetworks?: Membership[] | null }
  const rows = [...(nodeNetworks ?? [])].sort(
    (a, b) =>
      (a.created_at ?? '').localeCompare(b.created_at ?? '') || a.network_id.localeCompare(b.network_id)
  )
  return {
    ...(rest as Omit<R, 'nodeNetworks'>),
    networkIds: rows.map(membership => membership.network_id),
    networkNames: Object.fromEntries(rows.flatMap(({ network_id, name }) => (name ? [[network_id, name]] : []))),
  }
}

/**
 * A realtime event on `nodes` as a `NodeRecord`. ⚠⚠ `node_networks` is not in the realtime
 * publication, so an event carries no memberships: a node the store holds keeps the ones it has,
 * because `update` merges. Only a node the store does not hold takes the one network on its row,
 * until the read that its event starts (`membershipMoved`, `useNode.subscribe`).
 */
export const withEventNetworks = <R extends object>(
  row: R,
  held: boolean
): Omit<R, 'networkId'> & { networkIds?: string[] } => {
  const { networkId, ...rest } = row as R & { networkId?: string | null }
  return held ? rest : { ...rest, networkIds: networkId ? [networkId] : [] }
}

/**
 * Whether a realtime event says that the store's memberships are old. The triggers move
 * `nodes.network_id` at a first attach, a last detach and a network delete, so a value outside
 * `networkIds` is another session's change. ⚠ `undefined` is a row with no such column
 * (router#2142), which says nothing.
 */
export const membershipMoved = (networkIds: string[], column: string | null | undefined): boolean =>
  column !== undefined && (column === null ? networkIds.length > 0 : !networkIds.includes(column))

/**
 * The memberships of a read over the rows a newer event kept (`rowsAfterRead`): an event says
 * nothing about them, so the read is always the fresher answer.
 */
export const withReadNetworks = <N extends { id?: string; networkIds: string[]; networkNames?: Record<string, string> }>(
  rows: N[],
  read: N[]
): N[] => {
  const byId = new Map(read.map(row => [row.id, row]))
  return rows.map(row => {
    const answer = byId.get(row.id)
    return answer ? { ...row, networkIds: answer.networkIds, networkNames: answer.networkNames } : row
  })
}

/** The name of a node in one network: `node_networks.name` when it is set, else its own name. */
export const nodeNameIn = (
  node: { name: string; networkNames?: Record<string, string> },
  networkId: string | null | undefined
): string => (networkId && node.networkNames?.[networkId]) || node.name
